import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient, get } from '@/api/client';

export type AccessPermission = 'view' | 'export' | 'manage' | 'schedule';
export type GrantState = 'allow' | 'deny' | 'inherit';

export interface AccessResource {
  id: number;
  slug: string;
  name: string;
  parent: string | null;
  resource_type: 'section' | 'page' | 'tab' | 'card' | 'chart' | 'table' | 'action' | 'api';
  is_assignable: boolean;
  order: number;
  path: string;
  group: string;
  permissions: Record<AccessPermission, boolean>;
}

export interface AccessResponse {
  version: number;
  is_superuser: boolean;
  has_any_viewable_resource: boolean;
  permission_keys: AccessPermission[];
  resources: AccessResource[];
}

export interface PrincipalUser {
  id: number;
  name?: string;
  username?: string;
  phone_number?: string;
  email?: string;
  role?: string;
}

export interface BIPrincipalsResponse {
  roles: { id: string; label: string }[];
  stores: { id: number; name: string }[];
  users: PrincipalUser[];
}

export interface GrantRow {
  resource_slug: string;
  permissions: Partial<Record<AccessPermission, GrantState>>;
}

export interface GrantsResponse {
  role?: string;
  user?: PrincipalUser;
  store_ids: number[];
  grants: GrantRow[];
}

export interface AuditResponse {
  results: {
    id: number;
    created_at: string;
    actor: string | null;
    target_type: 'role' | 'user';
    target_role: string | null;
    target_user: number | null;
    resource_slug: string;
    before: Partial<Record<AccessPermission, GrantState>>;
    after: Partial<Record<AccessPermission, GrantState>>;
  }[];
}

export const ACCESS_QUERY_KEY = ['bi-access'] as const;
export const ADMIN_RESOURCES_QUERY_KEY = ['bi-access-admin', 'resources'] as const;
export const ADMIN_PRINCIPALS_QUERY_KEY = ['bi-access-admin', 'principals'] as const;

export function useAccessQuery(enabled: boolean) {
  return useQuery({
    queryKey: ACCESS_QUERY_KEY,
    queryFn: () => get<AccessResponse>('/feature/bi/api/access/'),
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useAdminBIResources(enabled: boolean) {
  return useQuery({
    queryKey: ADMIN_RESOURCES_QUERY_KEY,
    queryFn: () => get<{ permission_keys: AccessPermission[]; resources: AccessResource[] }>('/feature/bi/api/admin/resources/'),
    enabled,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useAdminBIPrincipals(enabled: boolean) {
  return useQuery({
    queryKey: ADMIN_PRINCIPALS_QUERY_KEY,
    queryFn: () => get<BIPrincipalsResponse>('/feature/bi/api/admin/principals/'),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useRoleGrants(role: string | null) {
  return useQuery({
    queryKey: ['bi-access-admin', 'role-grants', role],
    queryFn: () => get<GrantsResponse>(`/feature/bi/api/admin/grants/roles/${role}/`),
    enabled: !!role,
    staleTime: 30_000,
  });
}

export function useUserGrants(userId: number | null) {
  return useQuery({
    queryKey: ['bi-access-admin', 'user-grants', userId],
    queryFn: () => get<GrantsResponse>(`/feature/bi/api/admin/grants/users/${userId}/`),
    enabled: !!userId,
    staleTime: 30_000,
  });
}

export function useAccessAudit(enabled: boolean) {
  return useQuery({
    queryKey: ['bi-access-admin', 'audit'],
    queryFn: () => get<AuditResponse>('/feature/bi/api/admin/audit/'),
    enabled,
    staleTime: 30_000,
  });
}

export function useSaveRoleGrants(role: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ grants, storeIds }: { grants: GrantRow[]; storeIds: number[] }) => {
      if (!role) throw new Error('Role is required');
      const r = await apiClient.put<GrantsResponse>(`/feature/bi/api/admin/grants/roles/${role}/`, {
        grants,
        store_ids: storeIds,
      });
      return r.data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['bi-access-admin', 'role-grants', role] }),
        qc.invalidateQueries({ queryKey: ['bi-access-admin', 'audit'] }),
        qc.invalidateQueries({ queryKey: ACCESS_QUERY_KEY }),
      ]);
    },
  });
}

export function useSaveUserGrants(userId: number | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ grants, storeIds }: { grants: GrantRow[]; storeIds: number[] }) => {
      if (!userId) throw new Error('User is required');
      const r = await apiClient.put<GrantsResponse>(`/feature/bi/api/admin/grants/users/${userId}/`, {
        grants,
        store_ids: storeIds,
      });
      return r.data;
    },
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['bi-access-admin', 'user-grants', userId] }),
        qc.invalidateQueries({ queryKey: ['bi-access-admin', 'audit'] }),
        qc.invalidateQueries({ queryKey: ACCESS_QUERY_KEY }),
      ]);
    },
  });
}
